import { useTranslation } from 'react-i18next';
import { useCurrentUserContext } from '@/contexts';
import { usePersonalPlanState } from '@/features/billing/hooks/usePersonalPlanState';
import { useSubscription } from '@/features/billing/hooks/useSubscription';
import { Flex } from '@/ui';
import { PanelContent } from '../../components/PanelContent';
import { PanelHeader } from '../../components/PanelHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { FamilyPromoPanel } from './components/FamilyPromoPanel';
import { InlineUpgradeChoices } from './components/InlineUpgradeChoices';
import { PersonalPlanCard } from './components/PersonalPlanCard';

// This is the user's Personal Account panel: their plan/billing is what funds
// their personal org, so it lives here rather than a standalone "Plans" tab.
// Every upgrade path is inline — the compare-plans modal is only for upgrade
// gates reached from outside settings.
export const Subscription = () => {
  const { t } = useTranslation('settingsModal', { keyPrefix: 'subscription' });
  const { currentUser } = useCurrentUserContext();
  const displayName = currentUser?.name || currentUser?.email;

  const { subscriptionCurrentPeriodEnd } = useSubscription();
  const planState = usePersonalPlanState();

  // Everyone with Plus who isn't already inside a family can add it — including
  // comped users and users whose Plus comes from a business org. Members of
  // someone else's family are left out on purpose: "buy your own family group"
  // isn't a story worth telling them.
  const canOfferFamily =
    planState.kind === 'plus' ||
    planState.kind === 'plus-ending' ||
    planState.kind === 'plus-manual' ||
    (planState.kind === 'inherited' && planState.workspace === 'business');

  // Who gets the inline pricing grid. A lapsed plan and a lapsed workspace both
  // need a way back, and a Free user needs a way in. `dunning` is deliberately
  // excluded: their subscription still exists and still bills if the payment
  // recovers, so selling them another one double-charges — the card's "Update
  // payment method" is the only correct action there.
  const canOfferPurchase =
    planState.kind === 'free' ||
    planState.kind === 'lapsed' ||
    planState.kind === 'inherited-lapsed';

  return (
    <>
      <PanelHeader>{displayName || t('heading')}</PanelHeader>
      <PanelContent>
        <SectionHeader first>{t('section_heading')}</SectionHeader>
        <PersonalPlanCard
          endDate={subscriptionCurrentPeriodEnd}
          state={planState}
        />

        {canOfferPurchase ? (
          <InlineUpgradeChoices />
        ) : canOfferFamily ? (
          <FamilyPromoPanel isManual={planState.kind === 'plus-manual'} />
        ) : null}

        {/* Keeps short panels from collapsing the plan card against the footer */}
        <Flex flex="1" />
      </PanelContent>
    </>
  );
};
